/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — notes-rules.ts
   Detection rules, disclosure rule engine, pattern matching for
   PPE/debt/intangibles/leases, rich schedule builders
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { rows, hasPriorData, cur, fmt, esc, meta, extractYear } from './utils';
import { buildComparativeSection } from './statements';
import { DEBT_PATTERN } from './patterns';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface DataContext {
  ppeTable: string;
  debtTable: string;
  equityTable: string;
}

export interface CheckItem {
  id: string;
  detected: boolean;
  required: boolean;
  disclosed: boolean;
}

export interface Detection {
  id: string;
  label: string;
  detected: boolean;
  required: boolean;
  reason: string;
  disclosed: boolean;
  asc: string;
  guidance: string;
  url: string;
}

// ─── Rich Schedule Builders ────────────────────────────────────────────────

export function buildPPESchedule(): string {
  const multi = hasPriorData();
  const c = cur();
  const m = meta();
  const curYear = (m.period ? extractYear(m.period) : null) || 'Current';
  const priorYear = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';

  // Gather gross assets and their accumulated depreciation
  const grossAssets = rows(state.currentData, 'noncurrent-assets').filter(r =>
    /property|equipment|furniture|vehicle|building|land|leasehold|computer|machinery/i.test(r.label) &&
    !/accumulated|depreciation|amortization/i.test(r.label)
  );
  const accumDep = rows(state.currentData, 'noncurrent-assets').filter(r =>
    /accumulated.*depreciation|accum.*dep/i.test(r.label)
  );

  if (grossAssets.length === 0) return '';

  let html = '<table class="note-table"><thead><tr><th></th><th>' + esc(curYear) + '</th>';
  if (multi) html += '<th>' + esc(priorYear) + '</th>';
  html += '</tr></thead><tbody>';

  let totalGross = 0, pTotalGross = 0;
  grossAssets.forEach(r => {
    const amt = parseFloat(String(r.amount)) || 0;
    totalGross += amt;
    let pAmt = 0;
    if (multi) {
      const match = rows(state.priorData, 'noncurrent-assets').find(p => p.label === r.label);
      if (match) pAmt = parseFloat(String(match.amount)) || 0;
      pTotalGross += pAmt;
    }
    html += '<tr><td>' + esc(r.label) + '</td><td>' + c + fmt(amt) + '</td>';
    if (multi) html += '<td>' + c + fmt(pAmt) + '</td>';
    html += '</tr>';
  });

  html += '<tr class="nt-subtotal"><td>Total cost</td><td>' + c + fmt(totalGross) + '</td>';
  if (multi) html += '<td>' + c + fmt(pTotalGross) + '</td>';
  html += '</tr>';

  let totalAccum = 0, pTotalAccum = 0;
  accumDep.forEach(r => {
    const amt = parseFloat(String(r.amount)) || 0;
    totalAccum += amt;
    let pAmt = 0;
    if (multi) {
      const match = rows(state.priorData, 'noncurrent-assets').find(p => p.label === r.label);
      if (match) pAmt = parseFloat(String(match.amount)) || 0;
      pTotalAccum += pAmt;
    }
    html += '<tr><td>' + esc(r.label) + '</td><td>' + c + fmt(amt) + '</td>';
    if (multi) html += '<td>' + c + fmt(pAmt) + '</td>';
    html += '</tr>';
  });

  if (accumDep.length > 1) {
    html += '<tr class="nt-subtotal"><td>Total accumulated depreciation</td><td>' + c + fmt(totalAccum) + '</td>';
    if (multi) html += '<td>' + c + fmt(pTotalAccum) + '</td>';
    html += '</tr>';
  }

  const net = totalGross + totalAccum; // accumDep is negative
  const pNet = pTotalGross + pTotalAccum;
  html += '<tr class="nt-total"><td>Property and equipment, net</td><td>' + c + fmt(net) + '</td>';
  if (multi) html += '<td>' + c + fmt(pNet) + '</td>';
  html += '</tr>';

  html += '</tbody></table>';
  return html;
}

export function buildInventorySchedule(): string {
  const multi = hasPriorData();
  const c = cur();
  const m = meta();
  const curYear = (m.period ? extractYear(m.period) : null) || 'Current';
  const priorYear = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';

  const invItems = rows(state.currentData, 'current-assets').filter(r => /inventor/i.test(r.label));
  if (invItems.length < 2) return ''; // Only show schedule if multiple inventory types

  let html = '<table class="note-table"><thead><tr><th></th><th>' + esc(curYear) + '</th>';
  if (multi) html += '<th>' + esc(priorYear) + '</th>';
  html += '</tr></thead><tbody>';

  let total = 0, pTotal = 0;
  invItems.forEach(r => {
    const amt = parseFloat(String(r.amount)) || 0;
    total += amt;
    let pAmt = 0;
    if (multi) {
      const match = rows(state.priorData, 'current-assets').find(p => p.label === r.label);
      if (match) pAmt = parseFloat(String(match.amount)) || 0;
      pTotal += pAmt;
    }
    html += '<tr><td>' + esc(r.label) + '</td><td>' + c + fmt(amt) + '</td>';
    if (multi) html += '<td>' + c + fmt(pAmt) + '</td>';
    html += '</tr>';
  });

  html += '<tr class="nt-total"><td>Total inventory</td><td>' + c + fmt(total) + '</td>';
  if (multi) html += '<td>' + c + fmt(pTotal) + '</td>';
  html += '</tr></tbody></table>';
  return html;
}

export function buildDebtSchedule(): string {
  const multi = hasPriorData();
  const c = cur();
  const m = meta();
  const curYear = (m.period ? extractYear(m.period) : null) || 'Current';
  const priorYear = (m.priorPeriod ? extractYear(m.priorPeriod) : null) || 'Prior';
  const curDebt = rows(state.currentData, 'current-liab').filter(r => DEBT_PATTERN.test(r.label));
  const ltDebt = rows(state.currentData, 'noncurrent-liab').filter(r => DEBT_PATTERN.test(r.label));
  if (curDebt.length === 0 && ltDebt.length === 0) return '';

  let html = '<table class="note-table"><thead><tr><th></th><th>' + esc(curYear) + '</th>';
  if (multi) html += '<th>' + esc(priorYear) + '</th>';
  html += '</tr></thead><tbody>';

  let total = 0, pTotal = 0;

  function addRows(items: any[], section: string): void {
    items.forEach((r: any) => {
      const amt = Math.abs(parseFloat(String(r.amount)) || 0);
      total += amt;
      let pAmt = 0;
      if (multi) {
        const match = rows(state.priorData, section).find((p: any) => p.label === r.label);
        if (match) pAmt = Math.abs(parseFloat(String(match.amount)) || 0);
        pTotal += pAmt;
      }
      html += '<tr><td>' + esc(r.label) + '</td><td>' + c + fmt(amt) + '</td>';
      if (multi) html += '<td>' + c + fmt(pAmt) + '</td>';
      html += '</tr>';
    });
  }

  if (ltDebt.length > 0) addRows(ltDebt, 'noncurrent-liab');
  if (curDebt.length > 0) {
    html += '<tr><td colspan="' + (multi ? 3 : 2) + '" style="font-weight:600;padding-top:8px">Less: current portion</td></tr>';
    addRows(curDebt, 'current-liab');
  }

  html += '<tr class="nt-total"><td>Total debt obligations</td><td>' + c + fmt(total) + '</td>';
  if (multi) html += '<td>' + c + fmt(pTotal) + '</td>';
  html += '</tr></tbody></table>';
  return html;
}

// ─── Default Disclosure Language for Missing Items ─────────────────────────

export function getDefaultDisclosureText(id: string, m: { company: string; period: string; priorPeriod: string }, dataContext: DataContext): string {
  const company: string = esc(m.company || 'The Company');
  const period: string = esc(m.period || 'the reporting period');
  const defaults: Record<string, string> = {
    'basis': '<h3>Note — Basis of Accounting</h3>' +
      '<p>The accompanying financial statements of ' + company + ' have been prepared on the accrual basis of accounting in accordance with accounting principles generally accepted in the United States of America (U.S. GAAP).</p>',

    'estimates': '<h3>Note — Use of Estimates</h3>' +
      '<p>The preparation of financial statements in conformity with U.S. GAAP requires management to make estimates and assumptions that affect the reported amounts of assets and liabilities and disclosure of contingent assets and liabilities at the date of the financial statements and the reported amounts of revenues and expenses during the reporting period. Actual results could differ from those estimates.</p>',

    'cash': '<h3>Note — Cash and Cash Equivalents</h3>' +
      '<p>The Company considers all highly liquid investments with an original maturity of three months or less when purchased to be cash equivalents. Cash balances are maintained in financial institutions, which at times may exceed federally insured limits.</p>',

    'revenue': '<h3>Note — Revenue Recognition</h3>' +
      '<p>The Company recognizes revenue in accordance with ASC 606. Revenue is recognized when control of promised goods or services is transferred to customers, in an amount that reflects the consideration the Company expects to be entitled to in exchange for those goods or services.</p>',

    'receivables': '<h3>Note — Accounts Receivable</h3>' +
      '<p>Accounts receivable are stated at the amount the Company expects to collect. The Company evaluates the collectability of receivables on a regular basis and establishes an allowance for doubtful accounts when deemed necessary based on historical experience and current economic conditions. Receivables are written off when deemed uncollectible.</p>',

    'inventory': '<h3>Note — Inventory</h3>' +
      '<p>Inventory is stated at the lower of cost or net realizable value. The Company periodically reviews inventory for obsolescence and writes down inventory to its net realizable value when the carrying amount exceeds the estimated selling price less costs to complete and sell.</p>',

    'ppe': '<h3>Note — Property and Equipment</h3>' +
      '<p>Property and equipment are recorded at cost less accumulated depreciation. Depreciation is computed using the straight-line method over the estimated useful lives of the respective assets. Expenditures for major improvements and additions are capitalized, while expenditures for maintenance and repairs are charged to expense as incurred. Upon retirement or disposal, the cost and accumulated depreciation are removed from the accounts, and any resulting gain or loss is reflected in the statements of operations.</p>' +
      (dataContext.ppeTable || ''),

    'intangibles': '<h3>Note — Intangible Assets</h3>' +
      '<p>Intangible assets with finite useful lives are amortized over their estimated useful lives on a straight-line basis. The Company reviews intangible assets for impairment whenever events or changes in circumstances indicate that the carrying amount may not be recoverable. Goodwill and indefinite-lived intangible assets are tested for impairment annually or more frequently if indicators of impairment exist.</p>',

    'debt': '<h3>Note — Debt and Notes Payable</h3>' +
      '<p>The Company\'s outstanding debt obligations are recorded at their principal amount. Interest expense is recognized as incurred. The Company was in compliance with all applicable debt covenants as of the balance sheet date.</p>' +
      (dataContext.debtTable || ''),

    'leases': '<h3>Note — Leases</h3>' +
      '<p>The Company determines if an arrangement is a lease at inception. Operating lease right-of-use assets and liabilities are recognized at the lease commencement date based on the present value of lease payments over the lease term. The Company has elected the practical expedient to not recognize right-of-use assets and lease liabilities for short-term leases with a term of 12 months or less.</p>',

    'taxes': '<h3>Note — Income Taxes</h3>' +
      '<p>The Company evaluates uncertain tax positions using a more-likely-than-not threshold for financial statement recognition. Management has evaluated the Company\'s tax positions and concluded that there are no uncertain tax positions requiring recognition as of the balance sheet date. The Company\'s tax returns remain subject to examination by taxing authorities for the standard statute of limitations period.</p>',

    'equity': '<h3>Note — Equity</h3>' +
      '<p>The following table summarizes the components of equity:</p>' +
      (dataContext.equityTable || ''),

    'related': '<h3>Note — Related Party Transactions</h3>' +
      '<p>The Company has evaluated its relationships with related parties. There were no material related party transactions during ' + period + ' that require disclosure.</p>',

    'concentrations': '<h3>Note — Concentrations of Credit Risk</h3>' +
      '<p>Financial instruments that potentially subject the Company to concentrations of credit risk consist principally of cash deposits and accounts receivable. The Company maintains its cash balances in financial institutions, which at times may exceed federally insured limits of $250,000 per depositor per institution. The Company has not experienced any losses on such accounts and management believes the Company is not exposed to any significant credit risk on its cash balances.</p>',

    'contingencies': '<h3>Note — Commitments and Contingencies</h3>' +
      '<p>In the normal course of business, the Company may be subject to various claims and legal proceedings. Management is not aware of any pending or threatened litigation that would have a material adverse effect on the Company\'s financial position, results of operations, or cash flows as of the date of these financial statements.</p>',

    'subsequent': '<h3>Note — Subsequent Events</h3>' +
      '<p>The Company has evaluated subsequent events through the date the financial statements were available to be issued. No material subsequent events have occurred since the balance sheet date that require recognition or disclosure in the financial statements.</p>',

    'goingconcern': '<h3>Note — Going Concern</h3>' +
      '<p>The accompanying financial statements have been prepared assuming that the Company will continue as a going concern. Management has evaluated the Company\'s ability to continue as a going concern and believes that the Company\'s existing resources and ongoing operations are sufficient to fund its operations for at least twelve months from the date these financial statements are issued.</p>',
  };
  return defaults[id] || '';
}

// ─── Build data context for disclosure generation ─────────────────────────

export function buildDataContext(): DataContext {
  return {
    ppeTable: buildPPESchedule(),
    debtTable: buildDebtSchedule(),
    equityTable: buildComparativeSection('equity'),
  };
}
